import { Link } from "react-router-dom";
import { useBrand, resolveBrandLogo } from "@/context/BrandContext";
import { useSlugScope } from "@/lib/scoped";

/**
 * Colourful football mark.
 * Pure SVG — no external assets, fully scalable.
 */
function BallMark({ size = 44 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="shrink-0 transition-transform duration-500 group-hover:rotate-[35deg]"
    >
      {/* Ball outline */}
      <circle cx="32" cy="32" r="29" fill="#FFFFFF" stroke="#0B1220" strokeWidth="2.5" />

      {/* Center pentagon — trophy gold */}
      <polygon
        points="32,15 44,24 39.5,38 24.5,38 20,24"
        fill="#FFD400"
        stroke="#0B1220"
        strokeWidth="2"
        strokeLinejoin="round"
      />

      {/* Surrounding hex panels in vivid colours */}
      {/* Top panel — red */}
      <path
        d="M32 15 L20 24 L13 18 L18 8 L30 6 Z"
        fill="#E11D2E"
        stroke="#0B1220"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {/* Top-right — royal blue */}
      <path
        d="M32 15 L44 24 L51 18 L46 8 L34 6 Z"
        fill="#0038FF"
        stroke="#0B1220"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {/* Right — green */}
      <path
        d="M44 24 L39.5 38 L50 44 L57 32 L51 18 Z"
        fill="#00A859"
        stroke="#0B1220"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {/* Bottom — purple */}
      <path
        d="M39.5 38 L24.5 38 L22 52 L34 56 L50 44 Z"
        fill="#7C3AED"
        stroke="#0B1220"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {/* Left — orange */}
      <path
        d="M24.5 38 L20 24 L13 18 L7 32 L14 44 Z"
        fill="#FF6A00"
        stroke="#0B1220"
        strokeWidth="2"
        strokeLinejoin="round"
      />

      {/* small stitch accents on the outer rim */}
      <circle cx="32" cy="3.5" r="1.2" fill="#0B1220" />
      <circle cx="32" cy="60.5" r="1.2" fill="#0B1220" />
      <circle cx="3.5" cy="32" r="1.2" fill="#0B1220" />
      <circle cx="60.5" cy="32" r="1.2" fill="#0B1220" />
    </svg>
  );
}

export function Logo({ size = 44, variant = "default", to }) {
  // 'default' for header (compact), 'large' for login/register splash
  const isLarge = variant === "large";
  const { brand } = useBrand() || {};
  // Slug-aware default target — when the Logo is rendered inside a
  // `/c/[slug]/*` client environment and no explicit `to` was passed, we
  // link back to `/c/[slug]` rather than the platform-wide `/`. Callers
  // that need a specific target (e.g. Header passing a pre-scoped path)
  // still override this via the `to` prop.
  const slug = useSlugScope();
  const resolvedTo = to ?? (slug ? `/c/${slug}` : "/");
  // When a client brand is active, swap the Dugsy mark for the client logo
  // and their name — preserves the "Football Challenge" tag as an anchor
  // to the underlying platform.
  if (brand) {
    // Larger image on the login/register splash so the pub logo really pops.
    const logoSize = isLarge ? 96 : size;
    const brandLogoSrc = resolveBrandLogo(brand);
    // Subtitle: always "Football Challenge" so every branded slug header
    // reads "<Pub Name> · Football Challenge" — user preference (Feb '26).
    // The `brand.town` field is still stored on the pub but no longer
    // rendered in the header.
    const subtitle = "Football Challenge";
    return (
      <Link
        to={resolvedTo}
        data-testid="brand-logo"
        className="inline-flex items-center gap-3 group"
        aria-label={brand.name}
      >
        {brandLogoSrc ? (
          <img
            src={brandLogoSrc}
            alt={brand.name}
            width={logoSize}
            height={logoSize}
            className="shrink-0 rounded-sm object-contain bg-white border border-zinc-200 p-0.5"
            style={{ width: logoSize, height: logoSize }}
          />
        ) : (
          <div
            className="shrink-0 rounded-sm flex items-center justify-center text-white font-bold"
            style={{ width: logoSize, height: logoSize, backgroundColor: brand.primary_color || "#0038FF" }}
          >
            {brand.name?.slice(0, 1).toUpperCase() || "P"}
          </div>
        )}
        <span className="flex flex-col leading-none">
          <span
            className={`font-heading font-extrabold tracking-tight text-zinc-950 ${
              isLarge ? "text-3xl sm:text-4xl" : "text-lg sm:text-xl"
            }`}
          >
            {brand.name}
          </span>
          <span
            className={`font-heading font-semibold uppercase text-zinc-500 ${
              isLarge ? "text-xs sm:text-sm tracking-[0.32em] mt-2" : "text-[9px] sm:text-[10px] tracking-[0.28em] mt-1"
            }`}
          >
            {subtitle}
          </span>
        </span>
      </Link>
    );
  }
  return (
    <Link
      to={resolvedTo}
      data-testid="dugsy-logo"
      className="inline-flex items-center gap-3 group"
      aria-label="Dugsy's Football Challenge"
    >
      <BallMark size={isLarge ? 56 : size} />
      <span className="flex flex-col leading-none">
        <span
          className={`font-heading font-extrabold tracking-tight text-zinc-950 ${
            isLarge ? "text-2xl sm:text-3xl" : "text-lg sm:text-xl"
          }`}
        >
          Dugsy&apos;s
        </span>
        <span
          className={`font-heading font-semibold uppercase text-zinc-500 ${
            isLarge ? "text-[11px] sm:text-xs tracking-[0.32em] mt-1.5" : "text-[9px] sm:text-[10px] tracking-[0.28em] mt-1"
          }`}
        >
          Football Challenge
        </span>
      </span>
    </Link>
  );
}
